import { useCallback, useMemo, useState } from 'react';
import {
  Document,
  DocumentType,
  GlobalActions,
  Practice,
  PracticeDocument
} from '../../model';
import {
  getContracts,
  getDispositions,
  getDocumentsListState,
  getDocumentsTypeFilters,
  getExpiredDocuments
} from '../../store/features/documents';
import { useAppSelector } from '../redux';

type DocumentsMap = { [Key in DocumentType]: DocumentItem };

export interface DocumentItem {
  items: Document[] | Practice<PracticeDocument>[];
  isLoading: boolean;
  isShown: boolean;
}

export interface DocumentsTabLogic {
  documents: DocumentsMap;
  flagOpenDetails: boolean;
  documentDetails: PracticeDocument;
  onOpenDetails: (documentDetails?: PracticeDocument) => void;
  onCloseDetails: () => void;
}

export const useDocumentsTabLogic = (_: GlobalActions): DocumentsTabLogic => {
  const [flagOpenDetails, setFlagOpenDetails] = useState(false);
  const [documentDetails, setDocumentDetails] = useState<PracticeDocument>();
  const contracts = useAppSelector(getContracts);
  const dispositionPractices = useAppSelector(getDispositions);
  const expiredDocumentsPractices = useAppSelector(getExpiredDocuments);
  const filters:any = useAppSelector(getDocumentsTypeFilters);
  const {
    contractsRequestStatus,
    dispositionsRequestStatus,
    expiredDocumentsRequestStatus,
  }:any = useAppSelector(getDocumentsListState);

  const documents: any = useMemo(
    () => ({
      Contract: {
        items: contracts,
        isLoading: contractsRequestStatus === 'loading',
        isShown: filters.Contract.show,
      },
      Disposition: {
        items: dispositionPractices,
        isLoading: dispositionsRequestStatus === 'loading',
        isShown: filters.Disposition.show,
      },
      ExpiredDocuments: {
        items: expiredDocumentsPractices,
        isLoading: expiredDocumentsRequestStatus === 'loading',
        isShown: filters.ExpiredDocuments.show,
      },
    }),
    [
      contracts,
      contractsRequestStatus,
      filters.Contract.show,
      filters.Disposition.show,
      filters.ExpiredDocuments.show,
      dispositionPractices,
      dispositionsRequestStatus,
      expiredDocumentsPractices,
      expiredDocumentsRequestStatus,
    ]
  );

  const onOpenDetails = useCallback((documentDetail: PracticeDocument) => {
    setFlagOpenDetails(true);
    setDocumentDetails(documentDetail);
  }, []);

  const onCloseDetails = useCallback(() => {
    setFlagOpenDetails(false);
    setDocumentDetails(undefined);
  }, []);

  return {
    documents,
    documentDetails,
    flagOpenDetails,
    onOpenDetails,
    onCloseDetails,
  };
};
